import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import StarsBackground from '@/components/StarsBackground';

const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    // Supabase auth sends users here via the recovery link. Depending on flow
    // (implicit vs PKCE) the session arrives via a hash fragment or a ?code= param.
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') setReady(true);
    });

    (async () => {
      // PKCE flow: exchange ?code= for a session
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      if (code) {
        const { error } = await supabase.auth.exchangeCodeForSession(code);
        if (!error) {
          setReady(true);
          // Clean code from URL
          window.history.replaceState({}, '', window.location.pathname);
          return;
        }
      }
      const { data } = await supabase.auth.getSession();
      if (data.session) setReady(true);
    })();

    return () => sub.subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) {
      toast({ title: 'Password too short', description: 'Use at least 8 characters.', variant: 'destructive' });
      return;
    }
    if (password !== confirm) {
      toast({ title: 'Passwords do not match', variant: 'destructive' });
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (error) {
      toast({ title: 'Could not update password', description: error.message, variant: 'destructive' });
      return;
    }
    toast({ title: 'Password updated', description: 'You can now sign in with your new password.' });
    await supabase.auth.signOut();
    navigate('/auth');
  };

  return (
    <div className="min-h-screen flex flex-col relative overflow-hidden bg-solr-space-darker">
      <div className="fixed inset-0 pointer-events-none" aria-hidden="true">
        <StarsBackground />
      </div>
      <div className="min-h-screen flex items-center justify-center p-4 relative z-10">
        <Card className="w-full max-w-md bg-solr-void-black/80 border-solr-nebula-purple backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-2xl text-center text-solr-starfield-white">Set a new password</CardTitle>
            <CardDescription className="text-center text-solr-starfield-white/70">
              {ready ? 'Choose a strong password for your account' : 'Validating your reset link…'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-solr-starfield-white">New password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={!ready}
                  className="bg-white/90 border-solr-nebula-purple text-black focus:bg-white"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm" className="text-solr-starfield-white">Confirm password</Label>
                <Input
                  id="confirm"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  disabled={!ready}
                  className="bg-white/90 border-solr-nebula-purple text-black focus:bg-white"
                />
              </div>
              <Button
                type="submit"
                disabled={loading || !ready}
                className="w-full bg-solr-nebula-purple hover:bg-solr-nebula-purple/80 text-solr-starfield-white"
              >
                {loading ? 'Updating…' : 'Update password'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword;
